import { createFileRoute, notFound } from "@tanstack/react-router";
import {
  School, BookOpen, HeartPulse, Pill, Truck, Warehouse,
  Users, Calendar, FileText, ShieldCheck, BarChart3, CreditCard,
  Workflow, MapPin, Bell, Package, Activity, Stethoscope, GraduationCap,
} from "lucide-react";
import { ServicePage, type ServicePageData } from "@/components/sections/service-page";

const CHILDREN: Record<string, ServicePageData & { metaDesc: string }> = {
  "school-management": {
    eyebrow: "Industry · Education",
    title: "School Management System",
    sub: "Run admissions, classes, fees and parent comms from one platform.",
    metaDesc: "School management software: admissions, attendance, timetables, exams, fees and parent portal.",
    intro:
      "An all-in-one platform for K-12 schools and academies — admissions, attendance, timetables, exams, fees, transport and a polished parent portal.",
    features: [
      { icon: GraduationCap, title: "Admissions", description: "Online forms, document upload and approvals." },
      { icon: Calendar, title: "Attendance & timetable", description: "Daily attendance, periods and substitutions." },
      { icon: FileText, title: "Exams & report cards", description: "Grading schemes, marksheets and transcripts." },
      { icon: CreditCard, title: "Fees & invoicing", description: "Term fees, discounts, online payments and reminders." },
      { icon: Users, title: "Parent portal", description: "Grades, attendance, fees and messaging in one app." },
      { icon: ShieldCheck, title: "Role-based access", description: "Admin, teacher, parent and student permissions." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Academic year, classes, fees and reports." },
      { step: "02", title: "Configure", description: "Branding, grading and approval workflows." },
      { step: "03", title: "Migrate", description: "Students, staff and historical fee balances." },
      { step: "04", title: "Launch", description: "Training for teachers, parents and admin." },
    ],
    deliverables: ["Configured SMS platform", "Parent + student apps", "Fee gateway", "Report-card templates", "Training"],
    techStack: ["Custom SMS", "Stripe / Stripe Atlas", "Supabase", "Twilio SMS", "WhatsApp Business"],
    pricing: [
      { name: "Small school", price: "$6k", features: ["Up to 500 students", "Standard reports", "Parent portal"] },
      { name: "Growing", price: "$15k", period: "project", highlighted: true, features: ["Up to 3k students", "Custom workflows", "Multi-branch"] },
      { name: "District", price: "Custom", features: ["Unlimited branches", "BI dashboards", "SSO + SIS integration"] },
    ],
    faqs: [
      { q: "Online payments included?", a: "Yes — Stripe, local cards and wallets, with auto-receipts." },
      { q: "Multi-language?", a: "Yes — English, Arabic, Urdu, Spanish and more, with RTL support." },
    ],
  },
  "lms-platform": {
    eyebrow: "Industry · Education",
    title: "LMS Platform",
    sub: "Deliver courses, assessments and certificates at any scale.",
    metaDesc: "Learning Management System (LMS): course authoring, assessments, certificates, cohorts and analytics.",
    intro:
      "Author courses, run cohorts, grade assessments and issue certificates — for academies, corporate training and online schools. SCORM/xAPI, live classes and gamification included.",
    features: [
      { icon: BookOpen, title: "Course authoring", description: "Video, quizzes, assignments and SCORM imports." },
      { icon: Users, title: "Cohorts & groups", description: "Run scheduled cohorts with discussion and live classes." },
      { icon: FileText, title: "Assessments", description: "Quizzes, written assignments and proctoring." },
      { icon: ShieldCheck, title: "Certificates", description: "Auto-issued, signed certificates with verification URLs." },
      { icon: Activity, title: "Analytics", description: "Learner progress, completion and drop-off insights." },
      { icon: Workflow, title: "Integrations", description: "Zoom, Google Meet, Stripe, HRIS and SSO." },
    ],
    process: [
      { step: "01", title: "Curriculum", description: "Map courses, modules and assessment design." },
      { step: "02", title: "Build", description: "Author, import or migrate from existing LMS." },
      { step: "03", title: "Launch", description: "Instructor training and pilot cohort." },
      { step: "04", title: "Scale", description: "Marketing, sales and learner analytics." },
    ],
    deliverables: ["Branded LMS", "Course templates", "Certificate engine", "Payments integration", "Reporting suite"],
    techStack: ["Custom LMS", "Mux video", "Stripe", "Zoom API", "Supabase", "SCORM/xAPI"],
    pricing: [
      { name: "Starter", price: "$5k", features: ["Up to 500 learners", "10 courses", "Standard reports"] },
      { name: "Pro", price: "$15k", period: "project", highlighted: true, features: ["Up to 10k learners", "Cohorts + live", "Certifications"] },
      { name: "Enterprise", price: "Custom", features: ["White-label", "SSO + SCORM", "Custom integrations"] },
    ],
    faqs: [
      { q: "Can we migrate from Moodle or Teachable?", a: "Yes — courses, learners, progress and certificates." },
      { q: "Can we sell courses?", a: "Yes — Stripe checkout, coupons, subscriptions and B2B licenses." },
    ],
  },
  "hospital-management": {
    eyebrow: "Industry · Healthcare",
    title: "Hospital Management",
    sub: "OPD, IPD, lab, pharmacy and billing on one HIS.",
    metaDesc: "Hospital Information System (HIS): OPD, IPD, EMR, lab, pharmacy, billing and insurance.",
    intro:
      "A complete hospital information system — appointments, EMR, OPD/IPD workflows, lab, pharmacy, billing, insurance and analytics. HIPAA-ready hosting included.",
    features: [
      { icon: Stethoscope, title: "EMR / EHR", description: "Structured patient records with templates per specialty." },
      { icon: Calendar, title: "OPD & IPD", description: "Appointments, admissions, wards and beds." },
      { icon: Activity, title: "Lab & radiology", description: "Order management, results and PACS integration." },
      { icon: Pill, title: "Pharmacy", description: "Inventory, prescriptions and dispensing." },
      { icon: CreditCard, title: "Billing & insurance", description: "Multi-payer billing with claim management." },
      { icon: ShieldCheck, title: "HIPAA-ready", description: "Encryption, audit logs and BAA hosting." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Departments, workflows and compliance scope." },
      { step: "02", title: "Configure", description: "Specialties, templates, fees and approvals." },
      { step: "03", title: "Migrate", description: "Patients, history and outstanding balances." },
      { step: "04", title: "Go live", description: "Phased rollout per department." },
    ],
    deliverables: ["Configured HIS", "EMR templates", "Lab/pharmacy modules", "Insurance setup", "Training"],
    techStack: ["Custom HIS", "HL7 / FHIR", "HIPAA hosting", "Supabase", "Stripe / payer gateways"],
    pricing: [
      { name: "Clinic chain", price: "$15k", features: ["Up to 5 sites", "Core modules", "Standard reports"] },
      { name: "Hospital", price: "$60k", period: "project", highlighted: true, features: ["Full HIS", "Lab + pharmacy", "Insurance billing"] },
      { name: "Enterprise", price: "Custom", features: ["Multi-hospital", "PACS + HL7", "BI + AI insights"] },
    ],
    faqs: [
      { q: "HIPAA compliance?", a: "Yes — encrypted hosting, BAAs, audit logs and access controls." },
      { q: "Integrations with labs/pharmacy?", a: "Yes — HL7, FHIR and major lab/pharmacy networks." },
    ],
  },
  "clinic-pharmacy": {
    eyebrow: "Industry · Healthcare",
    title: "Clinic & Pharmacy",
    sub: "Lightweight EMR, appointments and pharmacy POS for small practices.",
    metaDesc: "Clinic and pharmacy software: EMR, appointments, e-prescriptions, pharmacy POS and inventory.",
    intro:
      "Right-sized for clinics and standalone pharmacies — appointments, EMR, e-prescriptions, pharmacy POS, inventory and patient SMS reminders.",
    features: [
      { icon: Calendar, title: "Appointments", description: "Online booking, calendar and SMS reminders." },
      { icon: Stethoscope, title: "EMR", description: "Patient profiles, vitals, notes and history." },
      { icon: FileText, title: "E-prescriptions", description: "Templates, refills and digital signatures." },
      { icon: Pill, title: "Pharmacy POS", description: "Sell OTC and Rx with stock and expiry tracking." },
      { icon: Package, title: "Inventory", description: "Lot, expiry and reorder point automation." },
      { icon: Bell, title: "Patient reminders", description: "SMS + WhatsApp for visits, refills and recalls." },
    ],
    process: [
      { step: "01", title: "Setup", description: "Specialties, templates and pharmacy catalog." },
      { step: "02", title: "Hardware", description: "POS, printer and label printer if needed." },
      { step: "03", title: "Train", description: "Doctor, receptionist and pharmacist." },
      { step: "04", title: "Launch", description: "Go-live with day-one support." },
    ],
    deliverables: ["Clinic + pharmacy platform", "EMR templates", "Pharmacy POS", "Patient SMS", "Training"],
    techStack: ["Custom EMR", "Stripe Terminal", "Twilio", "Supabase"],
    pricing: [
      { name: "Solo clinic", price: "$3k", features: ["1 doctor", "Up to 2k patients", "Online booking"] },
      { name: "Clinic + pharmacy", price: "$9k", period: "project", highlighted: true, features: ["Multi-doctor", "Pharmacy POS", "Inventory"] },
      { name: "Multi-branch", price: "Custom", features: ["Centralized reporting", "Insurance billing", "Custom workflows"] },
    ],
    faqs: [
      { q: "Online appointments?", a: "Yes — embeddable widget and patient app with reminders." },
      { q: "Expiry tracking for pharmacy?", a: "Yes — FEFO selling and expiry-loss reports." },
    ],
  },
  "fleet-management": {
    eyebrow: "Industry · Logistics",
    title: "Fleet Management",
    sub: "Track vehicles, optimize routes and prove delivery in real time.",
    metaDesc: "Fleet management software: GPS tracking, route optimization, driver app, proof of delivery and maintenance.",
    intro:
      "Live GPS, route optimization, driver app with proof of delivery, vehicle maintenance and fuel reporting — for fleets of 5 to 5,000 vehicles.",
    features: [
      { icon: MapPin, title: "Live GPS tracking", description: "Real-time location, geofences and ETA." },
      { icon: Workflow, title: "Route optimization", description: "Multi-stop routing with time windows." },
      { icon: Users, title: "Driver app", description: "Manifest, navigation, POD photo + signature." },
      { icon: Activity, title: "Vehicle maintenance", description: "Service schedules, faults and costs." },
      { icon: BarChart3, title: "Fuel & cost reports", description: "Per-vehicle and per-route economics." },
      { icon: Bell, title: "Customer ETA SMS", description: "Live tracking links sent to recipients." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Fleet size, routes, depots and SLAs." },
      { step: "02", title: "Install", description: "GPS units, driver apps and dispatcher console." },
      { step: "03", title: "Optimize", description: "Tune routes, geofences and KPIs." },
      { step: "04", title: "Operate", description: "Daily ops with weekly reviews." },
    ],
    deliverables: ["Dispatcher console", "Driver app", "GPS hardware", "POD workflow", "Fleet KPIs"],
    techStack: ["Custom FMS", "Google/Mapbox", "Teltonika trackers", "Twilio", "Supabase"],
    pricing: [
      { name: "Small fleet", price: "$4k", features: ["Up to 10 vehicles", "Dispatcher + driver app", "Basic reports"] },
      { name: "Growing", price: "$12k", period: "project", highlighted: true, features: ["Up to 50 vehicles", "Route optimization", "POD"] },
      { name: "Enterprise", price: "Custom", features: ["Unlimited fleet", "Multi-depot", "Custom integrations"] },
    ],
    faqs: [
      { q: "Bring our own trackers?", a: "Yes — we integrate with most major brands or supply Teltonika." },
      { q: "Customer tracking link?", a: "Yes — live ETA link via SMS or email, no signup required." },
    ],
  },
  "warehouse-management": {
    eyebrow: "Industry · Logistics",
    title: "Warehouse Management",
    sub: "Run a paperless warehouse with WMS-grade workflows.",
    metaDesc: "Warehouse Management System (WMS): receiving, putaway, picking, packing, cycle counts and dock scheduling.",
    intro:
      "Receive, putaway, pick, pack and ship faster — with barcode-driven workflows, wave picking and live dashboards. Scales from a single warehouse to a national network.",
    features: [
      { icon: Warehouse, title: "Receiving + putaway", description: "ASN, dock scheduling and directed putaway." },
      { icon: Package, title: "Wave / batch picking", description: "Optimize picker paths and workload." },
      { icon: Workflow, title: "Pack + ship", description: "Cartonization and carrier label printing." },
      { icon: ShieldCheck, title: "Cycle counts", description: "Scheduled counts with variance reports." },
      { icon: BarChart3, title: "Live dashboards", description: "Throughput, accuracy and SLA at a glance." },
      { icon: Bell, title: "Slotting", description: "ABC analysis and bin re-slotting suggestions." },
    ],
    process: [
      { step: "01", title: "Discovery", description: "Layout, SKUs, throughput and integrations." },
      { step: "02", title: "Setup", description: "Zones, bins, scanners and label printers." },
      { step: "03", title: "Pilot", description: "Run one zone end-to-end." },
      { step: "04", title: "Scale", description: "Roll out across the warehouse." },
    ],
    deliverables: ["WMS configured", "Scanner + label setup", "Pick paths", "Cycle-count plan", "Dashboards"],
    techStack: ["Custom WMS", "Zebra scanners", "Carrier APIs (UPS/FedEx/DHL)", "Supabase"],
    pricing: [
      { name: "Single warehouse", price: "$8k", features: ["Up to 5k SKUs", "Receiving + picking", "Standard reports"] },
      { name: "Multi-zone", price: "$20k", period: "project", highlighted: true, features: ["Wave picking", "Cartonization", "Carrier integration"] },
      { name: "Enterprise", price: "Custom", features: ["Multi-warehouse", "Automation/MHE integration", "Slotting AI"] },
    ],
    faqs: [
      { q: "Does it integrate with ERP?", a: "Yes — bi-directional with ERPNext, Odoo, NetSuite and custom systems." },
      { q: "Carrier label printing?", a: "Yes — UPS, FedEx, DHL, USPS and regional carriers." },
    ],
  },
};

export const Route = createFileRoute("/software/industry-solutions/$child")({
  loader: ({ params }) => {
    const item = CHILDREN[params.child];
    if (!item) throw notFound();
    return { item, slug: params.child };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [] };
    const { item } = loaderData;
    const url = `/software/industry-solutions/${params.child}`;
    return {
      meta: [
        { title: `${item.title} — Industry Solutions | CoderLity` },
        { name: "description", content: item.metaDesc },
        { property: "og:title", content: `${item.title} — Industry Solutions | CoderLity` },
        { property: "og:description", content: item.metaDesc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: IndustryChild,
});

function IndustryChild() {
  const { item } = Route.useLoaderData();
  return <ServicePage data={item} />;
}
